if (!customElements.get('product-carousel-element')) {
  class ProductCarouselElement extends HTMLElement {
    constructor() {
      super();
      this.initialized = false;
      this._observer = null;
    }

    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this._renderSkeletons();

      if ('IntersectionObserver' in window) {
        this._observer = new IntersectionObserver((entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              this.fetchProducts();
              this._observer.unobserve(this);
              this._observer = null;
            }
          });
        }, { rootMargin: '200px' });
        this._observer.observe(this);
      } else {
        this.fetchProducts();
      }
    }

    disconnectedCallback() {
      this._observer?.disconnect();
      this._observer = null;
    }

    get sectionSelector() {
      return '';
    }

    buildUrl() {
      return this.dataset.url || null;
    }

    _renderSkeletons() {
      const count = parseInt(this.dataset.productsToShow, 10) || 4;
      const columnsMobile = Math.min(Math.max(parseInt(this.dataset.columnsMobile, 10) || 1, 1), 2);
      const columnsTablet = Math.min(Math.max(parseInt(this.dataset.columnsTablet, 10) || 1, 1), 4);
      const columnsDesktop = Math.min(Math.max(parseInt(this.dataset.columnsDesktop, 10) || 1, 1), 8);
      const forceGridMobile = this.dataset.forceGridMobile === 'true';
      const forceGridTablet = this.dataset.forceGridTablet === 'true';
      const forceGridDesktop = this.dataset.forceGridDesktop === 'true';
      const peekMobileClass = this.dataset.peekMobile === 'true' && !forceGridMobile ? ' slider--peek-mobile' : '';
      const peekTabletClass = this.dataset.peekTablet === 'true' && !forceGridTablet ? ' slider--peek-tablet' : '';
      const peekDesktopClass = this.dataset.peekDesktop === 'true' && !forceGridDesktop ? ' slider--peek-desktop' : '';
      const fullBleedClass = this.dataset.fullWidth === 'true' ? ' slider--full-bleed' : '';
      const forceGridMobileClass = forceGridMobile ? ' forced-grid-mobile' : '';
      const forceGridTabletClass = forceGridTablet ? ' forced-grid-tablet' : '';
      const forceGridDesktopClass = forceGridDesktop ? ' forced-grid-desktop' : '';
      const mediaRatioMap = { square: 'var(--ratio-square)', portrait: 'var(--ratio-portrait)', landscape: 'var(--ratio-landscape)' };
      const mediaRatio = mediaRatioMap[this.dataset.mediaAspectRatio] || 'var(--ratio-portrait)';

      const items = Array.from({ length: count }, (_, i) => `
        <div class="slider__item" role="group" aria-roledescription="slide"${i === 0 ? ' data-index="0"' : ''}>
          <span class="skeleton skeleton--product-card" role="presentation" aria-hidden="true">
            <span class="skeleton skeleton--product-card__image"></span>
            <span class="skeleton skeleton--product-card__title"></span>
            <span class="skeleton skeleton--product-card__price"></span>
          </span>
        </div>
      `).join('');

      this.innerHTML = `
        <div class="theme-slider slider--${columnsMobile}-col-mobile slider--${columnsTablet}-col-tablet slider--${columnsDesktop}-col-desktop${peekMobileClass}${peekTabletClass}${peekDesktopClass}${fullBleedClass}${forceGridMobileClass}${forceGridTabletClass}${forceGridDesktopClass}" style="--skeleton-media-ratio: ${mediaRatio};" aria-busy="true" aria-live="polite">
          <div class="slider__list">${items}</div>
        </div>
      `;
    }

    async fetchProducts() {
      const url = this.buildUrl();
      if (!url) {
        this.hideSection();
        return;
      }

      try {
        const html = await theme.cache.request(url);
        const doc = theme.utils.domParser.parseFromString(html, 'text/html');
        const newContent = doc.querySelector(this.tagName.toLowerCase());

        if (newContent && newContent.innerHTML.trim()) {
          this.innerHTML = newContent.innerHTML;
          this.initializeSlider();
          this.showSection();
        } else {
          this.hideSection();
        }
      } catch {
        this.hideSection();
      }
    }

    initializeSlider() {
      const slider = this.querySelector('theme-slider');
      if (slider && typeof slider.setUpSlider === 'function') {
        slider.initObserver?.disconnect();
        slider.initObserver = null;
        slider.setUpSlider();
      }
    }

    hideSection() {
      this.innerHTML = '';
      const section = this.closest(this.sectionSelector);
      if (section) section.style.display = 'none';
    }

    showSection() {
      const section = this.closest(this.sectionSelector);
      if (section) section.style.display = '';
    }
  }

  customElements.define('product-carousel-element', ProductCarouselElement);
}
